/**
 * Centralized application configuration.
 * Environment-sensitive values read from import.meta.env.
 * UI/UX constants defined here rather than scattered in hooks.
 */
export const APP_CONFIG = {
  auth: {
    maxRetries: Number(import.meta.env.VITE_AUTH_MAX_RETRIES ?? 3),
    retryDelayMs: Number(import.meta.env.VITE_AUTH_RETRY_DELAY_MS ?? 1500),
  },
  query: {
    /** Data is considered fresh for 1 minute before background refetch */
    staleTime: 60_000,
    /** Unused query data kept in cache for 10 minutes */
    gcTime: 10 * 60_000,
    /** Role/permission data rarely changes — cache for 1 hour */
    roleStaleTime: 60 * 60_000,
    /** Real-time-sensitive data (steps, materials) — 15 second freshness */
    realtimeStaleTime: 15_000,
    /** Photo metadata rarely changes — 5 minute freshness */
    photoStaleTime: 5 * 60_000,
    /** Signed URLs valid for 1hr — cache 30min (half expiry), gc at 55min */
    signedUrlStaleTime: 30 * 60_000,
    signedUrlGcTime: 55 * 60_000,
  },
  pagination: {
    activityLogPageSize: Number(import.meta.env.VITE_ACTIVITY_LOG_PAGE_SIZE ?? 50),
  },
  undo: {
    timeoutMs: 8_000,
    maxEntries: 20,
  },
  pricing: {
    /**
     * Master flag for the Stripe Checkout flow. When false, paid tiers fall
     * back to the waitlist/contact dialogs.
     *
     * #1778 — Defaults to ON (#235 + #1282 are complete). The
     * VITE_STRIPE_CHECKOUT_ENABLED env var is kept as an explicit kill
     * switch: set it to the literal string 'false' in a build environment
     * to disable checkout there without a code revert. Any other value
     * (including unset) leaves checkout on.
     *
     * `canCheckout()` in `useCreateCheckoutSession.ts` still independently
     * gates on the requested tier having a non-null `stripe_price_id_*`,
     * so this flag being on does not bypass the price-id-backfill check.
     */
    checkoutEnabled: import.meta.env.VITE_STRIPE_CHECKOUT_ENABLED !== 'false',
  },
  stores: {
    'Home Depot': {
      color: '#F96302',
      keywords: [
        'lumber', 'wood', 'plywood', 'drywall', 'sheetrock', 'osb', 'stud', '2x4', '2x6', '2x8',
        'concrete', 'cement', 'mortar', 'grout', 'tile', 'flooring', 'hardwood', 'laminate',
        'roofing', 'shingle', 'siding', 'insulation', 'deck', 'fence', 'post',
        'door', 'window', 'trim', 'molding', 'baseboard', 'crown',
      ],
    },
    "Lowe's": {
      color: '#004990',
      keywords: [
        'paint', 'primer', 'stain', 'brush', 'roller', 'tape', 'caulk', 'sealant',
        'pipe', 'plumbing', 'faucet', 'toilet', 'sink', 'shower', 'valve', 'fitting', 'pvc', 'copper',
        'vanity', 'cabinet', 'countertop', 'backsplash',
        'appliance', 'washer', 'dryer', 'dishwasher', 'refrigerator',
      ],
    },
    'Ace Hardware': {
      color: '#D40029',
      keywords: [
        'screw', 'nail', 'bolt', 'nut', 'anchor', 'hinge', 'latch', 'lock', 'knob', 'handle',
        'glue', 'adhesive', 'epoxy', 'sandpaper', 'putty', 'filler',
        'key', 'chain', 'rope', 'hook',
      ],
    },
    'Amazon': {
      color: '#FF9900',
      keywords: [
        'smart', 'sensor', 'camera', 'thermostat', 'wifi', 'bluetooth', 'led', 'strip',
        'tool', 'drill', 'saw', 'sander', 'level', 'measure', 'clamp',
        'safety', 'glove', 'goggles', 'mask', 'ear',
      ],
    },
  } as Record<string, { color: string; keywords: string[] }>,
  storeColors: {
    'Home Depot': '#F96302',
    "Lowe's": '#004990',
    'Ace Hardware': '#D40029',
    'Amazon': '#FF9900',
    'Other': '#6B7280',
  } as Record<string, string>,
} as const;
